/**
 * Folders of the drawings library: tree building and operations.
 *
 * Folders are synced as plain metadata (the most recent change wins), so the
 * tree may temporarily be inconsistent across devices: a parent deleted
 * elsewhere, or a loop created by two devices moving folders into each other.
 * `buildFolderTree()` repairs both at display time (orphans go to the root,
 * loops are broken at their most recently moved folder).
 */

import { moveDrawings, newDrawingId, trashDrawings } from "./drawings";
import { DrawingStore, emitLibraryChange } from "./store";

import type { DrawingId, DrawingMeta, Folder, FolderId } from "./types";

export type FolderTree = {
  /** live (not deleted) folders */
  byId: Map<FolderId, Folder>;
  parentOf: (id: FolderId) => FolderId | null;
  /** sub-folders sorted by name (key null: root) */
  childrenOf: (id: FolderId | null) => Folder[];
  /** folders from the root down to `id` (included) */
  pathOf: (id: FolderId | null) => Folder[];
  /** `id` itself and all the folders below it */
  subtreeOf: (id: FolderId) => Set<FolderId>;
  /** existing folder, or null (root) for missing/deleted ones */
  resolve: (id: FolderId | null | undefined) => FolderId | null;
};

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const buildFolderTree = (folders: readonly Folder[]): FolderTree => {
  const byId = new Map(
    folders.filter((folder) => !folder.deleted).map((f) => [f.id, f]),
  );
  const parents = new Map<FolderId, FolderId | null>();
  for (const folder of byId.values()) {
    parents.set(
      folder.id,
      folder.parentId && byId.has(folder.parentId) ? folder.parentId : null,
    );
  }
  // break loops
  for (const start of byId.keys()) {
    const seen: FolderId[] = [];
    let current: FolderId | null = start;
    while (current && !seen.includes(current)) {
      seen.push(current);
      current = parents.get(current) ?? null;
    }
    if (current) {
      const loop = seen.slice(seen.indexOf(current));
      const latest = loop.reduce((a, b) =>
        byId.get(b)!.updatedAt > byId.get(a)!.updatedAt ? b : a,
      );
      parents.set(latest, null);
    }
  }

  const children = new Map<FolderId | null, Folder[]>();
  for (const folder of byId.values()) {
    const parentId = parents.get(folder.id) ?? null;
    children.set(parentId, [...(children.get(parentId) ?? []), folder]);
  }
  for (const list of children.values()) {
    list.sort((a, b) => collator.compare(a.name, b.name));
  }

  const tree: FolderTree = {
    byId,
    parentOf: (id) => parents.get(id) ?? null,
    childrenOf: (id) => children.get(id) ?? [],
    pathOf: (id) => {
      const path: Folder[] = [];
      let current = id && byId.has(id) ? id : null;
      while (current) {
        path.unshift(byId.get(current)!);
        current = parents.get(current) ?? null;
      }
      return path;
    },
    subtreeOf: (id) => {
      const result = new Set<FolderId>();
      const visit = (folderId: FolderId) => {
        result.add(folderId);
        tree.childrenOf(folderId).forEach((child) => visit(child.id));
      };
      visit(id);
      return result;
    },
    resolve: (id) => (id && byId.has(id) ? id : null),
  };
  return tree;
};

export const loadFolderTree = async () =>
  buildFolderTree(await DrawingStore.listFolders());

const saveFolder = async (folder: Folder) => {
  await DrawingStore.putFolder(folder);
};

export const createFolder = async (name: string, parentId: FolderId | null) => {
  const now = Date.now();
  const folder: Folder = {
    id: newDrawingId(),
    name,
    parentId,
    createdAt: now,
    updatedAt: now,
    deleted: false,
    dirty: true,
  };
  await saveFolder(folder);
  emitLibraryChange([folder.id], "library");
  return folder.id;
};

const updateFolders = async (
  ids: readonly FolderId[],
  changes: (folder: Folder) => Partial<Folder>,
) => {
  const changed: FolderId[] = [];
  const now = Date.now();
  for (const id of ids) {
    const folder = await DrawingStore.getFolder(id);
    if (folder) {
      await saveFolder({
        ...folder,
        ...changes(folder),
        updatedAt: now,
        dirty: true,
      });
      changed.push(id);
    }
  }
  emitLibraryChange(changed, "library");
};

export const renameFolder = (id: FolderId, name: string) =>
  updateFolders([id], () => ({ name }));

/** whether the folders can be moved into `targetId` (not into themselves or
 * their own sub-folders) */
export const canMoveFoldersInto = (
  tree: FolderTree,
  folderIds: readonly FolderId[],
  targetId: FolderId | null,
) => !targetId || folderIds.every((id) => !tree.subtreeOf(id).has(targetId));

/** moves drawings and folders into `targetId` (null: root) */
export const moveItems = async (
  items: { drawingIds: readonly DrawingId[]; folderIds: readonly FolderId[] },
  targetId: FolderId | null,
) => {
  const tree = await loadFolderTree();
  const folderIds = items.folderIds.filter(
    (id) =>
      tree.byId.has(id) &&
      tree.parentOf(id) !== targetId &&
      canMoveFoldersInto(tree, [id], targetId),
  );
  if (folderIds.length) {
    await updateFolders(folderIds, () => ({ parentId: targetId }));
  }
  if (items.drawingIds.length) {
    await moveDrawings(items.drawingIds, targetId);
  }
};

export type FolderContents = {
  /** drawings (not in trash) in the folder and its sub-folders */
  drawings: DrawingMeta[];
  /** sub-folders at any depth */
  folders: FolderId[];
};

export const getFolderContents = (
  tree: FolderTree,
  drawings: readonly DrawingMeta[],
  folderId: FolderId,
): FolderContents => {
  const subtree = tree.subtreeOf(folderId);
  return {
    drawings: drawings.filter(
      (meta) =>
        !meta.deleted &&
        !meta.purged &&
        subtree.has(tree.resolve(meta.folderId) as FolderId),
    ),
    folders: [...subtree].filter((id) => id !== folderId),
  };
};

/**
 * Deletes folders. Their drawings are either moved to the trash together with
 * all the sub-folders ("trash"), or moved with the sub-folders into the
 * parent folder ("moveUp", the root for top-level folders).
 */
export const deleteFolders = async (
  ids: readonly FolderId[],
  mode: "trash" | "moveUp",
) => {
  const tree = await loadFolderTree();
  const drawings = (await DrawingStore.listMeta()).filter((m) => !m.purged);
  // nested selections are handled by their top-most selected folder
  const roots = ids.filter(
    (id) =>
      tree.byId.has(id) &&
      !tree
        .pathOf(id)
        .some((folder) => folder.id !== id && ids.includes(folder.id)),
  );

  for (const id of roots) {
    const parentId = tree.parentOf(id);
    if (mode === "trash") {
      const subtree = tree.subtreeOf(id);
      const inside = drawings.filter((meta) =>
        subtree.has(tree.resolve(meta.folderId) as FolderId),
      );
      await trashDrawings(
        inside.filter((meta) => !meta.deleted).map((meta) => meta.id),
      );
      // already trashed ones would otherwise be restored into a deleted
      // folder: they'd show at the root anyway, but keep it explicit
      await updateFolders([...subtree], () => ({ deleted: true }));
    } else {
      await moveDrawings(
        drawings
          .filter((meta) => tree.resolve(meta.folderId) === id)
          .map((meta) => meta.id),
        parentId,
      );
      await updateFolders(
        tree.childrenOf(id).map((child) => child.id),
        () => ({ parentId }),
      );
      await updateFolders([id], () => ({ deleted: true }));
    }
  }
};
